import { useState, type ReactElement } from "react";
import copy from "copy-to-clipboard";
import { CodeBlock } from "../../ui/CodeBlock/CodeBlock";
import { Icon } from "../../ui/Icon/Icon";
import { categoryOf, problemJson, statusText } from "./errors";
import { useErrorsStore, useSelectedCode } from "./store";
import styles from "./ErrorDetail.module.css";

export interface ErrorDetailProps {
  onOpenReference?: () => void;
}

export function ErrorDetail({
  onOpenReference,
}: ErrorDetailProps): ReactElement | null {
  const code = useSelectedCode();
  const problem = useErrorsStore((state) =>
    code ? (state.docs[code] ?? null) : null,
  );
  const [copiedJson, setCopiedJson] = useState(false);
  const [copiedLink, setCopiedLink] = useState(false);

  if (!problem) return null;

  // Narrowed here; capture the string so the closure doesn't see the nullable.
  const json = problemJson(problem);

  function copyJson() {
    copy(json);
    setCopiedJson(true);
    setTimeout(() => setCopiedJson(false), 1500);
  }

  function copyLink() {
    copy(window.location.href);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 1500);
  }

  return (
    <div className={styles.detail}>
      <h1 className={styles.title}>{problem.title}</h1>
      <div className={styles.subhead}>
        <button type="button" className={styles.code} onClick={onOpenReference}>
          {problem.code}
        </button>
        <button type="button" className={styles.share} onClick={copyLink}>
          <Icon name="copy" size={14} />
          {copiedLink ? "Copied" : "Share link"}
        </button>
      </div>

      <div className={styles.stats}>
        <div className={styles.stat}>
          <div className={styles.statLabel}>HTTP status</div>
          <div className={styles.statValue}>{statusText(problem.status)}</div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>Retryable</div>
          <div className={styles.statValue}>
            {problem.retryable ? "Yes" : "No"}
          </div>
        </div>
        <div className={styles.stat}>
          <div className={styles.statLabel}>Domain</div>
          <div className={styles.statValue}>{categoryOf(problem.code)}</div>
        </div>
      </div>

      <div className={styles.section}>
        <div className={styles.label}>Detail</div>
        <p className={styles.text}>{problem.detail}</p>
      </div>
      <div className={styles.section}>
        <div className={styles.label}>What to do</div>
        <p className={styles.text}>{problem.partnerAction}</p>
      </div>

      <div className={styles.section}>
        <div className={styles.refHead}>
          <div className={styles.label}>Error reference · application/problem+json</div>
          <button type="button" className={styles.copy} onClick={copyJson}>
            <Icon name="copy" size={14} />
            {copiedJson ? "Copied" : "Copy JSON"}
          </button>
        </div>
        <CodeBlock code={json} />
      </div>
    </div>
  );
}
